import type { V2ConversationTurn, WidgetBlock } from '@shared/conversation-schema';
import { WIDGET_TYPES } from '@shared/widget-schemas';
import {
  CARD_MOVED_WIDGET_TYPE,
  MATERIALIZED_CARD_ID_PREFIX,
} from '@/hooks/v2/widgetReplace';

/**
 * Scoping voice layout (017) — in voice mode the scoping thread is audio-first:
 * Anaka is heard, not read. It shows ONLY widgets (the visual cards audio can't
 * convey). Every plain-text turn — user or assistant, including Anaka's own
 * latest message — is dropped; AND within a surviving widget-carrying turn its
 * own text blocks are stripped too, so a `[text, widget…]` turn (the shape Fix B
 * / spec 032 mirrors Anaka's spoken response into) renders the widget alone, not
 * the spoken text beside it. Flipping to text shows the full turn (that path
 * renders the untouched `turns`, not this transform).
 *
 * Voice widget dedupe — within that widget-only view, a widget Anaka re-emits
 * shows ONCE, as its latest version, instead of stacking a stale copy next to
 * the update. We keep only the latest occurrence of each dedupe key (see
 * {@link widgetDedupKey}) and strip the earlier ones; a turn left with no
 * surviving widget is then dropped.
 *
 * The dedupe key is `widget_id` for most widgets, but for widget types that
 * represent a single re-emitted "current state" card rather than distinct
 * instances it's a fixed key instead: the scoping artifact card
 * (`sc_artifact_card`) keys on its KIND (`sc_artifact_card:decision`, `:bets`,
 * `:assumptions`, `:scope`…) since one widget_type covers many artifact kinds
 * and the model re-emits an accreting card of the same kind with a fresh
 * `widget_id` each turn; the scope summary/recap card (`sc_scope_summary_card`)
 * has no sub-kind at all, so every occurrence keys to the same literal string —
 * a summary re-shown on a restart/recap collapses onto the prior one instead of
 * stacking two copies. Any other widget_type falls back to `widget_id` (distinct
 * instances stay distinct).
 *
 * Presentation-only and voice-only: the full `turns` array is untouched in
 * state, so switching back to text immediately shows the complete history
 * (in text you can watch the conversation — and each card's contents — evolve).
 */
export function voiceFocusTurns(turns: V2ConversationTurn[]): V2ConversationTurn[] {
  // 004-p6 (T016) — the id-keyed replace helper leaves `card_moved` breadcrumb
  // chips for the TEXT thread. Voice is audio-first and already collapses each
  // card to its latest version in place, so a breadcrumb would be orphan noise —
  // strip markers before the widget-only view.
  const withoutMarkers = turns.map(stripCardMovedMarkers);
  const framed = removeLearntOnceFramed(withoutMarkers);
  const deduped = dedupeWidgetsByLatestId(framed);
  const widgetsOnly = deduped
    .filter((t) => t.content.some((b) => b.type === 'widget'))
    .map(stripNonWidgetBlocks);
  return orderWidgetsBySection(widgetsOnly);
}

/**
 * The scoping spine's card order. In voice the thread IS the card stack — there
 * is no prose between the cards to explain why one sits above another — so the
 * stack must read top-to-bottom in the order the call builds the scope:
 *
 *   what we knew → the decision → the bets it rests on → what we'll learn → the scope
 *
 * Without this the order is "least-recently-updated first", because the dedupe
 * below keeps each card at its LATEST occurrence: refine the decision at the bets
 * step and the decision card jumps BELOW the bets card. The founder watches the
 * structure reshuffle itself while they talk.
 *
 * Ranks are deliberately sparse-free integers matching the spine's own indices so
 * a new card kind slots in without renumbering. Unknown kinds sort LAST but keep
 * their relative order (stable), so a widget this map doesn't know about is never
 * hoisted above a scoping card it has no business preceding.
 */
const CARD_SECTION_RANK: Record<string, number> = {
  learnt: 0,
  decision: 1,
  bets: 2,
  assumptions: 2,
  bets_with_assumptions: 2,
  goals: 3,
  summary: 4,
  scope: 4,
};

const UNRANKED = Number.MAX_SAFE_INTEGER;

/**
 * Section rank for a widget block. Reads the card KIND from the same two places
 * {@link widgetDedupKey} does — a materialized card's deterministic
 * `sc-card:{mode}:{engagementId}:{kind}` id (kind is the last segment), else the
 * on-the-wire `data.payload.kind`. Exported for unit testing.
 */
export function cardSectionRank(block: WidgetBlock): number {
  if (block.widget_type === WIDGET_TYPES.SC_SCOPE_SUMMARY_CARD) return CARD_SECTION_RANK.summary;
  if (block.widget_type === WIDGET_TYPES.SC_LEARNT_CARD) return CARD_SECTION_RANK.learnt;

  if (block.widget_id.startsWith(MATERIALIZED_CARD_ID_PREFIX)) {
    // `sc-card:{mode}:{engagementId}:{kind}` — the kind is the final segment.
    // Split from the END so an engagement id containing a colon can't shift it.
    const kind = block.widget_id.slice(block.widget_id.lastIndexOf(':') + 1);
    const rank = CARD_SECTION_RANK[kind];
    if (rank !== undefined) return rank;
  }

  if (block.widget_type === 'sc_artifact_card') {
    const data = block.data as { kind?: unknown; payload?: { kind?: unknown } } | undefined;
    const kind = data?.payload?.kind ?? data?.kind;
    if (typeof kind === 'string') {
      const rank = CARD_SECTION_RANK[kind];
      if (rank !== undefined) return rank;
    }
  }

  return UNRANKED;
}

/**
 * Re-order the widget-only view into {@link CARD_SECTION_RANK} order — within each
 * turn, and across turns.
 *
 * Both passes are STABLE sorts, so cards of equal rank keep their arrival order
 * and an all-in-one-rank thread is returned untouched. Turns are ranked by their
 * MINIMUM card rank: a turn carrying several cards (the resume re-materialization
 * emits the whole reached set at once) sorts by its earliest section, and its own
 * contents were already ordered by the first pass.
 *
 * Referential stability: a turn whose content didn't move is returned as the same
 * object, and the array itself is returned untouched when nothing reordered — so
 * React sees no change and no card remounts when this is a no-op.
 */
function orderWidgetsBySection(turns: V2ConversationTurn[]): V2ConversationTurn[] {
  const withSortedContent = turns.map((turn) => {
    const ranked = turn.content.map((block, i) => ({
      block,
      i,
      rank: block.type === 'widget' ? cardSectionRank(block as WidgetBlock) : UNRANKED,
    }));
    const sorted = [...ranked].sort((a, b) => a.rank - b.rank || a.i - b.i);
    const moved = sorted.some((entry, i) => entry.i !== i);
    return moved ? { ...turn, content: sorted.map((e) => e.block) } : turn;
  });

  const rankedTurns = withSortedContent.map((turn, i) => {
    let min = UNRANKED;
    for (const block of turn.content) {
      if (block.type !== 'widget') continue;
      const rank = cardSectionRank(block as WidgetBlock);
      if (rank < min) min = rank;
    }
    return { turn, i, rank: min };
  });
  const sortedTurns = [...rankedTurns].sort((a, b) => a.rank - b.rank || a.i - b.i);
  const turnsMoved = sortedTurns.some((entry, i) => entry.i !== i);

  if (!turnsMoved) {
    // Nothing crossed a turn boundary — return the per-turn result, which is
    // already referentially identical wherever its own content didn't move.
    return withSortedContent.every((t, i) => t === turns[i]) ? turns : withSortedContent;
  }
  return sortedTurns.map((e) => e.turn);
}

/**
 * 004-p10 T021 (INV-10) — once the call has produced a card of its OWN, the opening
 * "what we already knew" card steps aside.
 *
 * In voice the thread IS the card stack: there is no prose between cards, and the
 * learnt card outranks every other (rank 0), so it would sit above the founder's
 * actual scope for the rest of the call. It has served its purpose the moment the
 * decision appears at framing.
 *
 * "The call's own card" means a ranked scoping card at frame or later — an unranked
 * widget from somewhere else must never displace it. Voice-only and render-time:
 * `turns` is untouched in state, so text still shows the full history including the
 * learnt card. Referentially stable when there is nothing to remove.
 */
function removeLearntOnceFramed(turns: V2ConversationTurn[]): V2ConversationTurn[] {
  const framed = turns.some((turn) =>
    turn.content.some((block) => {
      if (block.type !== 'widget') return false;
      const rank = cardSectionRank(block as WidgetBlock);
      return rank > CARD_SECTION_RANK.learnt && rank !== UNRANKED;
    }),
  );
  if (!framed) return turns;

  let removedAny = false;
  const next = turns.map((turn) => {
    const content = turn.content.filter(
      (b) => !(b.type === 'widget' && b.widget_type === WIDGET_TYPES.SC_LEARNT_CARD),
    );
    if (content.length === turn.content.length) return turn;
    removedAny = true;
    return { ...turn, content };
  });
  return removedAny ? next : turns;
}

/** Drop `card_moved` breadcrumb markers (text-only affordance); referentially stable when none present. */
function stripCardMovedMarkers(turn: V2ConversationTurn): V2ConversationTurn {
  const content = turn.content.filter(
    (b) => !(b.type === 'widget' && b.widget_type === CARD_MOVED_WIDGET_TYPE),
  );
  return content.length === turn.content.length ? turn : { ...turn, content };
}

/**
 * Keep only the widget blocks of a turn (drop text / any non-widget content) so
 * voice mode shows the visual card alone. Returns the original turn instance
 * when it was already widgets-only (referential stability for React).
 */
function stripNonWidgetBlocks(turn: V2ConversationTurn): V2ConversationTurn {
  const widgetsOnly = turn.content.filter((b) => b.type === 'widget');
  return widgetsOnly.length === turn.content.length ? turn : { ...turn, content: widgetsOnly };
}

/**
 * Dedupe identity for a widget block. For the scoping artifact card the identity
 * is the card KIND, so an accreting Decision / Bets / Bets+Assumptions card
 * re-emitted with a fresh `widget_id` collapses to its latest version rather than
 * stacking. The scope summary/recap card has no sub-kind — every occurrence IS
 * the same card — so it keys on its widget_type alone (a summary re-shown on a
 * restart recap collapses onto the prior one instead of stacking two copies).
 * Every other widget keeps its `widget_id` (distinct instances stay distinct).
 * Exported for unit testing.
 */
export function widgetDedupKey(block: WidgetBlock): string {
  // 004-p6 (R1.3) — a materialized roster card now carries a stable, engagement-
  // scoped `widget_id` (`sc-card:{mode}:{engagementId}:{kind}`) lifted from the
  // relay, so keying on that id IS id-parity: successive re-emits collapse to one.
  // The kind-based key below stays as the FALLBACK for legacy model-minted ids
  // (a fresh id per turn), so voice dedup keeps working before/without T013.
  if (block.widget_id.startsWith(MATERIALIZED_CARD_ID_PREFIX)) {
    return block.widget_id;
  }
  if (block.widget_type === 'sc_artifact_card') {
    // The card's kind lives at `data.payload.kind` on the wire (the widget's
    // props are `{ payload: { kind, … }, draft? }`; see ScArtifactCardPropsSchema
    // + normalizeScArtifactData). Read that first; fall back to a flat `data.kind`
    // only defensively. Reading `data.kind` alone (the earlier bug) always missed —
    // kind is never top-level — so every re-emit fell through to `widget_id` and
    // stacked instead of collapsing.
    const data = block.data as { kind?: unknown; payload?: { kind?: unknown } } | undefined;
    const kind = data?.payload?.kind ?? data?.kind;
    if (typeof kind === 'string' && kind.length > 0) {
      return `sc_artifact_card:${kind}`;
    }
  }
  if (block.widget_type === 'sc_scope_summary_card') {
    return 'sc_scope_summary_card';
  }
  return block.widget_id;
}

/**
 * Strip every widget block superseded by a later block with the same dedupe key
 * (see {@link widgetDedupKey}) anywhere in the thread, including later within the
 * same turn, keeping only each key's latest occurrence. Turn order, count and
 * roles are preserved; non-widget content is left intact. Returns the original
 * turn instance wherever nothing was stripped (referential stability for React).
 */
function dedupeWidgetsByLatestId(turns: V2ConversationTurn[]): V2ConversationTurn[] {
  const latest = new Map<string, { t: number; b: number }>();
  turns.forEach((turn, t) => {
    turn.content.forEach((block, b) => {
      if (block.type === 'widget') latest.set(widgetDedupKey(block), { t, b });
    });
  });

  return turns.map((turn, t) => {
    let stripped = false;
    const content = turn.content.filter((block, b) => {
      if (block.type !== 'widget') return true;
      const loc = latest.get(widgetDedupKey(block));
      const isLatest = loc !== undefined && loc.t === t && loc.b === b;
      if (!isLatest) stripped = true;
      return isLatest;
    });
    return stripped ? { ...turn, content } : turn;
  });
}
